
import React from 'react';
import { X, Crown, CreditCard, Clock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getActivePriceDisplay } from '../stripe-config';

interface TrialReminderPopupProps {
  daysLeft: number;
  onDismiss: () => void;
}

export function TrialReminderPopup({ daysLeft, onDismiss }: TrialReminderPopupProps) {
  const navigate = useNavigate();
  const pricing = getActivePriceDisplay();

  const handleUpgradeClick = () => {
    navigate('/dashboard/settings');
    onDismiss();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative">
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 text-slate hover:text-midnight"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mb-4">
            <Crown className="h-8 w-8 text-white" />
          </div>

          <h3 className="text-xl font-bold text-midnight mb-2">
            {daysLeft === 1 ? 'Trial Ends Tomorrow!' : `${daysLeft} Days Left in Trial`}
          </h3>

          {pricing.isFounders && (
            <div className="bg-gradient-to-r from-gold/20 to-yellow-100 border border-gold/30 rounded-lg px-4 py-2 mb-4 inline-flex items-center">
              <Sparkles className="h-4 w-4 text-gold mr-2" />
              <span className="text-sm font-semibold text-midnight">Founders Pricing — Limited Time!</span>
            </div>
          )}

          <p className="text-slate mb-6">
            {pricing.isFounders
              ? `Lock in $${pricing.monthly}/month (reg. $${pricing.regularMonthly}) for the lifetime of your subscription. This founders' rate won't last!`
              : "Don't lose access to premium features! Upgrade now to continue using unlimited AI analysis, advanced formatting, and sermon sharing."}
          </p>

          <div className="flex flex-col space-y-3">
            <button
              onClick={handleUpgradeClick}
              className="w-full bg-gradient-to-r from-electric to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:from-electric/90 hover:to-blue-600/90 transition-all duration-200 flex items-center justify-center"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              {pricing.isFounders ? 'Get Founders Pricing' : 'Upgrade to Pro'}
            </button>

            <button
              onClick={onDismiss}
              className="w-full text-slate hover:text-midnight py-2 px-4 rounded-lg border border-sand/20 hover:bg-sand/10 transition-colors"
            >
              Remind me later
            </button>
          </div>

          <div className="mt-4 text-xs text-slate">
            <div className="flex items-center justify-center">
              {pricing.isFounders ? (
                <>
                  <Sparkles className="h-3 w-3 mr-1" />
                  <span className="line-through opacity-60 mr-1">${pricing.regularMonthly}/month</span> ${pricing.monthly}/month
                </>
              ) : (
                <>
                  <Clock className="h-3 w-3 mr-1" />
                  Starting at ${pricing.monthly}/month
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
